import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { ArrowRightIcon } from 'lucide-react';
export function HomePage() {
  const {
    t
  } = useLanguage();
  return <motion.div initial={{
    opacity: 0,
    filter: 'blur(4px)'
  }} animate={{
    opacity: 1,
    filter: 'blur(0px)'
  }} exit={{
    opacity: 0,
    filter: 'blur(4px)'
  }} transition={{
    duration: 0.6,
    ease: [0.4, 0, 0.2, 1]
  }} data-id="element-421">
      <Hero data-id="element-422" />

      {/* About Teaser */}
      <section className="py-32 lg:py-44 bg-warmWhite warm-glow relative" data-id="element-423">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-10 text-center" data-id="element-424">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-5 mb-6" data-id="element-425">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand/50" data-id="element-426"></div>
            <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-427">
              {t.about.subtitle}
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand/50" data-id="element-428"></div>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          duration: 0.8
        }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-navy mb-10" data-id="element-429">
            {t.about.title}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="text-dark/55 font-light leading-[1.95] text-[15px] max-w-2xl mx-auto mb-12" data-id="element-430">
            {t.about.description1}
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          duration: 0.7
        }} data-id="element-431">
            <Link to="/about" className="inline-flex items-center gap-2.5 text-navy hover:text-sand transition-colors duration-400 font-medium text-sm tracking-wide group" data-id="element-432">
              {t.home.discoverMore}
              <ArrowRightIcon className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" data-id="element-433" />
            </Link>
          </motion.div>
        </div>
        {/* Section divider */}
        <div className="absolute bottom-0 left-0 right-0 section-divider" data-id="element-434" />
      </section>

      {/* Featured Dishes */}
      <section className="py-32 lg:py-44 bg-cream bg-sand-texture relative" data-id="element-435">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-436">
          <div className="text-center mb-20" data-id="element-437">
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex items-center justify-center gap-5 mb-6" data-id="element-438">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand/50" data-id="element-439"></div>
              <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-440">
                {t.home.featuredSubtitle}
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand/50" data-id="element-441"></div>
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.8
          }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-navy" data-id="element-442">
              {t.home.featuredTitle}
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-9 mb-16" data-id="element-443">
            {[{
            img: 'https://images.unsplash.com/photo-1534604973900-c43ab4c2e0ab?w=700',
            name: t.menu.items.starters[10].name,
            desc: t.menu.items.starters[10].desc,
            price: t.menu.items.starters[10].price
          }, {
            img: 'https://images.unsplash.com/photo-1534080564583-6be75777b70a?w=700',
            name: t.menu.items.rice[2].name,
            desc: t.menu.items.rice[2].desc,
            price: t.menu.items.rice[2].price
          }, {
            img: 'https://images.unsplash.com/photo-1558030006-450675393462?w=700',
            name: t.menu.items.meat[5].name,
            desc: t.menu.items.meat[5].desc,
            price: t.menu.items.meat[5].price
          }].map((dish, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 25
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.15,
            duration: 0.8,
            ease: [0.4, 0, 0.2, 1]
          }} className="bg-white rounded-sm overflow-hidden shadow-soft group hover:shadow-elegant transition-shadow duration-700" data-id="element-444">
                <div className="h-64 lg:h-72 overflow-hidden relative" data-id="element-445">
                  <img src={dish.img} alt={dish.name} className="w-full h-full object-cover transition-transform duration-[1.5s] ease-elegant group-hover:scale-105" data-id="element-446" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" data-id="element-447" />
                </div>
                <div className="p-10 text-center" data-id="element-448">
                  <h3 className="font-serif text-xl text-navy mb-3" data-id="element-449">
                    {dish.name}
                  </h3>
                  <p className="text-dark/45 text-sm font-light mb-5 leading-relaxed" data-id="element-450">
                    {dish.desc}
                  </p>
                  <span className="text-sand font-medium text-sm tracking-wide" data-id="element-451">
                    {dish.price}
                  </span>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-8" data-id="element-452">
            <Link to="/menu" className="inline-block px-12 py-4.5 bg-navy text-white hover:bg-navy-light transition-all duration-700 ease-elegant text-[11px] tracking-[0.2em] uppercase font-medium hover:shadow-elegant" data-id="element-453">
              {t.home.viewFullMenu}
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 section-divider" data-id="element-454" />
      </section>

      {/* Pool Teaser */}
      <section className="py-32 lg:py-44 bg-warmWhite relative overflow-hidden" data-id="element-455">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-456">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16 lg:gap-28" data-id="element-457">
            {/* Image Side */}
            <motion.div initial={{
            opacity: 0,
            x: 35
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 1,
            ease: [0.4, 0, 0.2, 1]
          }} className="w-full lg:w-6/12 relative" data-id="element-458">
              <div className="relative aspect-[4/3] w-full rounded-sm overflow-hidden shadow-elegant group" data-id="element-459">
                <img src="https://cdn.magicpatterns.com/uploads/noRonfkFvEGEVmZXgXRQ4r/download_(4).png" alt="El Astillero de noche" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] ease-elegant group-hover:scale-105" data-id="element-460" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/20 to-transparent" data-id="element-461" />
              </div>
              {/* Decorative accent frame */}
              <div className="absolute -bottom-6 -right-6 w-full h-full border border-sand/20 rounded-sm -z-10" data-id="element-462"></div>
            </motion.div>

            {/* Text Side */}
            <motion.div initial={{
            opacity: 0,
            x: -35
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 1,
            delay: 0.15,
            ease: [0.4, 0, 0.2, 1]
          }} className="w-full lg:w-6/12 flex flex-col justify-center" data-id="element-463">
              <div className="flex items-center gap-5 mb-8" data-id="element-464">
                <div className="h-px w-16 bg-gradient-to-r from-sand to-sand/30" data-id="element-465"></div>
                <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-466">
                  {t.pool?.subtitle || 'Relax mediterráneo'}
                </span>
              </div>

              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy mb-10 leading-[1.1]" data-id="element-467">
                {t.pool?.title || 'Piscina & Lounge'}
              </h2>

              <p className="text-dark/55 font-light leading-[2] text-[15px] mb-12" data-id="element-468">
                {t.pool?.description1 || 'Disfruta de nuestra exclusiva piscina rodeada de un entorno natural privilegiado. El complemento perfecto para una experiencia gastronómica inolvidable.'}
              </p>

              <div data-id="element-469">
                <Link to="/pool" className="inline-flex items-center gap-3 text-navy hover:text-sand transition-colors duration-500 font-medium text-sm tracking-wide group" data-id="element-470">
                  {t.home.discoverMore}
                  <ArrowRightIcon className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-2" data-id="element-471" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 section-divider" data-id="element-472" />
      </section>

      {/* CTA Banner — Sunset atmosphere */}
      <section className="relative py-44 lg:py-56 overflow-hidden" data-id="element-473">
        <div className="absolute inset-0 w-full h-full" data-id="element-474">
          <video autoPlay loop muted playsInline className="object-cover w-full h-full scale-105" data-id="element-475">
            <source src="https://videos.pexels.com/video-files/857251/857251-hd_1920_1080_25fps.mp4" type="video/mp4" data-id="element-476" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-[#2C1810]/50 to-navy/80 backdrop-blur-[2px]" data-id="element-477" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center" data-id="element-478">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-5 mb-7" data-id="element-479">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand-light/50" data-id="element-480"></div>
            <span className="text-sand-light text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-481">
              Restaurant
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand-light/50" data-id="element-482"></div>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          duration: 0.8
        }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-white mb-8" data-id="element-483">
            {t.home.ctaTitle}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="text-white/55 text-[15px] font-light mb-14 max-w-lg mx-auto leading-[1.9]" data-id="element-484">
            {t.home.ctaSubtitle}
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} data-id="element-485">
            <Link to="/reservations" className="inline-block px-14 py-5 bg-sand hover:bg-sand-dark text-navy font-medium text-[11px] tracking-[0.2em] uppercase transition-all duration-700 ease-elegant hover:shadow-warm hover:scale-[1.02]" data-id="element-486">
              {t.nav.bookNow}
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>;
}